import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import Client from "components/Client";
import Config from "components/ClientConfig";

export default class RegisterReturnPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client("none"),
      catalogsLoaded: false,
      productNameById: {},
      comboNameById: {},
      returnDlg: {
        step: 0, // 0=find order, 1=select lines, 2=success
        searchIref: "",
        loaded: false,
        sourceOrder: null,
        lines: [],
        select: {},
        busy: false,
        err: "",
        payType: 1,
        existingReturns: [],
        success: null,
      },
      snack: { open: false, msg: "", sev: "success" },
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  bootstrap = async () => {
    await this.loadDictionaries();
  };

  toast = (msg, sev = "success") => {
    if (this.props.onToast) this.props.onToast(msg, sev);
    else console.log(sev.toUpperCase(), msg);
  };

  resetForm = () => {
    this.setState((s) => ({
      returnDlg: {
        ...s.returnDlg,
        step: 0,
        searchIref: "",
        loaded: false,
        sourceOrder: null,
        lines: [],
        select: {},
        busy: false,
        err: "",
        payType: 1,
        existingReturns: [],
        success: null,
      },
    }));
  };

  loadDictionaries = async () => {
    const { client } = this.state;

    const pr =
      (await Promise.resolve(
        client.getData(Config.API_URL_PRODUCT + "list_products/", {
          orderBy_name: true,
        })
      )) || {};

    const pList = Array.isArray(pr) ? pr : pr.products || pr.results || pr.items || [];
    const productNameById = {};
    pList.forEach((p) => {
      const id = String(p?.iref ?? p?.id ?? "");
      if (id) productNameById[id] = p?.name || p?.label || id;
    });

    const cr =
      (await Promise.resolve(
        client.getData(Config.API_URL_COMBO + "list_combos/", {
          orderBy_name: true,
        })
      )) || {};

    const cList = Array.isArray(cr)
      ? cr
      : Array.isArray(cr.productCombos) && cr.productCombos.length
        ? cr.productCombos
        : Array.isArray(cr.combos) && cr.combos.length
          ? cr.combos
          : cr.results || cr.items || [];

    const comboNameById = {};
    cList.forEach((c) => {
      const id = String(c?.iref ?? c?.id ?? "");
      if (id) comboNameById[id] = c?.name || c?.label || id;
    });

    this.setState({ productNameById, comboNameById, catalogsLoaded: true });
  };

  extractApiError = (resOrErr) => {
    try {
      const r = resOrErr?.response || resOrErr;
      if (r?.status === "Error" || r?.status === "error") {
        const parts = [];
        if (r.code) parts.push(String(r.code));
        if (r.message) parts.push(String(r.message));
        const d = r.data;
        const dataMsg = (d && (d.message || d.detail || d.error || d.reason)) || null;
        if (dataMsg) parts.push(String(dataMsg));
        return parts.join(" — ");
      }
      if (r instanceof Error && r.message) return r.message;
      const d = r?.data;
      if (d && (d.message || d.detail || d.error)) {
        return String(d.message || d.detail || d.error);
      }
    } catch (_) {}
    return null;
  };

  findExistingReturns = async (sourceOrder) => {
    const { client } = this.state;
    const ref = sourceOrder?.code || "";
    if (!ref) return [];

    let date = sourceOrder?.date || null;
    if (date) {
      date = String(date).trim();
      if (/^\d{2}-\d{2}-\d{4}/.test(date)) {
        const [dd, mm, yyyy] = date.split("-");
        date = `${yyyy}-${mm}-${dd}`;
      } else if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
        date = date.slice(0, 10);
      } else {
        const d = new Date(date);
        date = isNaN(d) ? null : d.toISOString().slice(0, 10);
      }
    }

    const q = {
      documentDate__gte: date,
      description: `Return for order ${ref}`,
    };

    try {
      const resp = await Promise.resolve(client.getData(Config.API_URL_ORDERS, q));
      const list = Array.isArray(resp) ? resp : resp.orders || [];
      return list.filter((o) =>
        String(o.description || "").includes(`Return for order ${ref}`)
      );
    } catch {
      return [];
    }
  };

  loadReturnSourceByIref = async () => {
    this.setState((s) => ({
      returnDlg: { ...s.returnDlg, busy: true, err: "", success: null },
    }));

    const safeDone = (patch) =>
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: false, ...(patch || {}) },
      }));

    try {
      const irefTxt = String(this.state.returnDlg.searchIref || "").trim();
      if (!/^\d+$/.test(irefTxt)) {
        throw new Error("Order iref must be numeric.");
      }
      const iref = parseInt(irefTxt, 10);

      const { client, productNameById, comboNameById } = this.state;

      let data = null;
      try {
        data = await Promise.resolve(
          client.getData(Config.API_URL_ORDERS, { iref, status: [1, 2] })
        );
      } catch (_) {}

      let r =
        (data && (data.result || data.order || data.item)) ||
        (data && (data.orders || data.results || data.items)) ||
        data;

      if (Array.isArray(r)) {
        r = r.find((x) => String(x?.iref) === String(iref)) || r[0];
      }
      if (r && (r.result || r.order || r.item)) {
        r = r.result || r.order || r.item;
      }

      if (!r || typeof r !== "object") {
        throw new Error("Order not found or order is not complete.");
      }

      const allLines = [];

      const pushLine = (ln, type, labelPrefix = "") => {
        const product =
          ln?.ProductPrice?.product ??
          ln?.ProductPromotion?.product ??
          ln?.ProductCompany?.product ??
          ln?.product ??
          null;

        const combo =
          ln?.ComboPrice?.productCombo ??
          ln?.ComboPromotion?.productCombo ??
          ln?.ComboCompany?.productCombo ??
          ln?.productCombo ??
          null;

        const parentQty = Math.abs(Number(ln.quantity || 0)) || 1;
        let comboChildren = [];

        if (type === "combo" && Array.isArray(ln.OrderComboProductsEntry_OrderComboEntry)) {
          comboChildren = ln.OrderComboProductsEntry_OrderComboEntry.map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            unitQty: Number(ch.quantity || 0) / parentQty,
            unitVal: Number(ch.value || 0) / parentQty,
            unitVat: Number(ch.vat || 0) / parentQty,
          }));
        }

        if (
          type === "comboPromo" &&
          Array.isArray(ln.OrderComboPromotionProductsEntry_OrderComboPromotionEntry)
        ) {
          comboChildren = ln.OrderComboPromotionProductsEntry_OrderComboPromotionEntry.map(
            (ch) => ({
              product: ch.product,
              priceType: ch.priceType,
              priceList: ch.priceList ?? null,
              unitQty: Number(ch.quantity || 0) / parentQty,
              unitVal: Number(ch.value || 0) / parentQty,
              unitVat: Number(ch.vat || 0) / parentQty,
            })
          );
        }

        if (
          type === "comboCompany" &&
          Array.isArray(ln.OrderComboCompanyProductsEntry_OrderComboCompanyEntry)
        ) {
          comboChildren = ln.OrderComboCompanyProductsEntry_OrderComboCompanyEntry.map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            unitQty: Number(ch.quantity || 0) / parentQty,
            unitVal: Number(ch.value || 0) / parentQty,
            unitVat: Number(ch.vat || 0) / parentQty,
          }));
        }

        const dictName =
          (product && productNameById?.[String(product)]) ||
          (combo && comboNameById?.[String(combo)]) ||
          null;

        const label =
          labelPrefix +
          (ln.name ||
            ln.productName ||
            ln.comboName ||
            dictName ||
            (product ? `Product ${product}` : combo ? `Combo ${combo}` : "Line"));

        allLines.push({
          key: `${type}_${product || combo}_${allLines.length}`,
          label,
          type,
          product,
          combo,
          productCompany: ln?.ProductCompany?.company ?? null,
          comboCompany: ln?.ComboCompany?.company ?? null,
          qty: Number(ln.quantity || 0),
          value: Number(ln.value || 0),
          discountValue: Number(ln.discountValue || 0),
          vat: Number(ln.vat || 0),
          priceList:
            ln?.ProductPrice?.priceList ??
            ln?.ComboPrice?.priceList ??
            ln?.ProductCompany?.priceList ??
            ln?.ComboCompany?.priceList ??
            null,
          promotionList:
            ln?.ProductPromotion?.promotionList ??
            ln?.ComboPromotion?.promotionList ??
            null,
          comboChildren,
        });
      };

      (r.orderProductEntry_order || []).forEach((l) => pushLine(l, "product", ""));
      (r.orderComboEntry_order || []).forEach((l) => pushLine(l, "combo", ""));
      (r.orderProductPromotionEntry_order || []).forEach((l) =>
        pushLine(l, "prodPromo", "Promo ")
      );
      (r.orderComboPromotionEntry_order || []).forEach((l) =>
        pushLine(l, "comboPromo", "Promo ")
      );
      (r.orderProductCompanyEntry_order || []).forEach((l) =>
        pushLine(l, "productCompany", "")
      );

      (r.orderComboCompanyEntry_order || []).forEach((l) =>
        pushLine(l, "comboCompany", "")
      );

      if (!allLines.length) {
        throw new Error("This order has no lines to return.");
      }

      const sourceOrder = {
        iref: String(r.iref ?? iref),
        code: r.code || "",
        type: String(r.type ?? 0),
        clientIref: String(r?.client?.iref ?? ""),
        pos: r?.pos ?? null,
        date: r.documentDate || "",
        valueWVat: r.totalAmount ?? 0,
        valueWoVat: r.totalAmountWithoutVAT ?? 0,
        discountValue: r.discountValue ?? 0,
        discountValueWithoutVAT: r.discountValueWithoutVAT ?? 0,
      };

      let existing = [];
      try {
        existing = await this.findExistingReturns({
          code: sourceOrder.code,
          iref: sourceOrder.iref,
          date: sourceOrder.date,
        });
      } catch (_) {}

      if (existing.length > 0) {
        safeDone({
          loaded: true,
          step: 0,
          sourceOrder,
          lines: [],
          select: {},
          existingReturns: existing,
          err: "This order already has a return registered.",
        });
        return;
      }

      safeDone({
        loaded: true,
        step: 1,
        sourceOrder,
        lines: allLines,
        select: {},
        existingReturns: [],
        err: "",
      });

      try {
        const existing = await this.findExistingReturns({
          code: r.code || "",
          iref: String(r.iref ?? iref),
          date: r.documentDate || "",
        });

        this.setState((s) => ({
          returnDlg: { ...s.returnDlg, existingReturns: existing },
        }));
      } catch (_) {}
    } catch (e) {
      safeDone({ err: e?.message || "Load failed." });
    }
  };

  fillReturnAll = () => {
    this.setState((s) => {
      const sel = {};
      (s.returnDlg.lines || []).forEach((ln) => {
        sel[ln.key] = Math.max(0, Number(ln.qty || 0));
      });
      return { returnDlg: { ...s.returnDlg, select: sel } };
    });
  };

  clearReturnSelection = () => {
    this.setState((s) => ({ returnDlg: { ...s.returnDlg, select: {} } }));
  };

  getSelectedLines = () => {
    const { lines, select } = this.state.returnDlg;
    return lines.filter((ln) => Number(select[ln.key] || 0) > 0);
  };

  getTotalsPreview = () => {
    const { lines, select } = this.state.returnDlg;
    let gross = 0;
    let vat = 0;
    let discount = 0;
    let totalQty = 0;
    let selectedCount = 0;

    lines.forEach((ln) => {
      const q = Number(select[ln.key] || 0);
      if (q > 0) {
        selectedCount += 1;
        totalQty += q;
        const origQty = Math.abs(Number(ln.qty || 0)) || 1;
        gross += (Number(ln.value || 0) / origQty) * q;
        vat += (Number(ln.vat || 0) / origQty) * q;
        discount += (Number(ln.discountValue || 0) / origQty) * q;
      }
    });

    const net = gross - vat;
    return {
      selectedCount,
      totalQty,
      gross,
      vat,
      net,
      discount,
      isFullReturnCandidate: selectedCount === 0,
    };
  };

  buildReturnPayload = () => {
    const { returnDlg } = this.state;
    const { sourceOrder, lines, select, payType } = returnDlg;

    const chosen = lines
      .map((ln) => {
        const q = Number(select[ln.key] || 0);
        return q > 0 ? { ...ln, retQty: q } : null;
      })
      .filter(Boolean);

    const orderProductEntry_order = [];
    const orderComboEntry_order = [];
    const orderProductCompanyEntry_order = [];
    const orderComboCompanyEntry_order = [];
    const orderProductPromotionEntry_order = [];
    const orderComboPromotionEntry_order = [];

    const isFullReturn = chosen.length === 0;

    if (isFullReturn) {
      return {
        orderIref: Number(sourceOrder?.iref),
        payType: Number(payType || 1),
        createdOn: new Date().toISOString(),
      };
    }

    chosen.forEach((ln) => {
      const origQty = Math.abs(Number(ln.qty || 0)) || 1;
      const unitVal = Number(ln.value || 0) / origQty;
      const unitVat = Number(ln.vat || 0) / origQty;

      const max = Math.max(0, Number(ln.qty || 0));
      const retQty = Math.min(Math.abs(Number(ln.retQty)), max);

      const q = retQty;
      const lineVal = Math.round(unitVal * q * 1000000) / 1000000;
      const lineVat = Math.round(unitVat * q * 1000000) / 1000000;

      const common = { value: lineVal, quantity: q, vat: lineVat };
      const usePromo = !!ln.promotionList;

      if (ln.product && !ln.combo) {
        if (usePromo) {
          orderProductPromotionEntry_order.push({
            ...common,
            ProductPromotion: {
              promotionList: ln.promotionList,
              product: ln.product,
            },
          });
        } else if (ln.type === "productCompany") {
          orderProductCompanyEntry_order.push({
            ...common,
            ProductCompany: {
              priceList: ln.priceList,
              product: ln.product,
            },
          });
        } else {
          orderProductEntry_order.push({
            ...common,
            ProductPrice: {
              priceList: ln.priceList,
              product: ln.product,
            },
          });
        }
      } else if (ln.combo) {
        if (usePromo) {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboPromotionEntry_order.push({
            ...common,
            ComboPromotion: {
              productCombo: ln.combo,
              promotionList: ln.promotionList,
            },
            OrderComboPromotionProductsEntry_OrderComboPromotionEntry: children,
          });
        } else if (ln.type === "comboCompany") {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboCompanyEntry_order.push({
            ...common,
            ComboCompany: {
              priceList: ln.priceList,
              productCombo: ln.combo,
            },
            OrderComboCompanyProductsEntry_OrderComboCompanyEntry: children,
          });
        } else {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboEntry_order.push({
            ...common,
            ComboPrice: {
              productCombo: ln.combo,
              priceList: ln.priceList,
            },
            OrderComboProductsEntry_OrderComboEntry: children,
          });
        }
      }
    });

    return {
      orderIref: Number(sourceOrder?.iref),
      payType: Number(payType || 1),
      createdOn: new Date().toISOString(),
      orderProductEntry_order,
      orderComboEntry_order,
      orderProductCompanyEntry_order,
      orderComboCompanyEntry_order,
      orderProductPromotionEntry_order,
      orderComboPromotionEntry_order,
    };
  };

  submitReturn = async () => {
    if (this.state.returnDlg.existingReturns?.length > 0) {
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, err: "A return already exists for this order." },
      }));
      return;
    }

    try {
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: true, err: "" },
      }));

      const payload = this.buildReturnPayload();
      const url = `${Config.API_URL_ORDERS}returnOrder/`;

      const res = this.state.client.pushJson
        ? await this.state.client.pushJson(url, payload)
        : await this.state.client.pushData(url, payload);

      if (res?.status === "Error" || res?.status === "error") {
        const msg = this.extractApiError(res) || `Error: ${res?.code || "RETURN-FAIL"}`;
        throw new Error(msg);
      }

      this.toast(res?.message || "Return order created");

      this.setState((s) => ({
        returnDlg: {
          ...s.returnDlg,
          busy: false,
          err: "",
          step: 2,
          success: res,
        },
      }));

      if (this.props.onSuccess) this.props.onSuccess(res);
    } catch (e) {
      const msg = this.extractApiError(e) || e?.message || "Submit failed.";
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: false, err: msg },
      }));
    }
  };

  render() {
    const { returnDlg, catalogsLoaded } = this.state;
    const totals = this.getTotalsPreview();
    const success = returnDlg.success;

    const fieldSx = {
      '& .MuiInputBase-root': {
        height: 34,
        borderRadius: '12px',
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
        bgcolor: '#fff',
      },
      // iOS zoom prevention: only the typed/selected value
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
      },
      // for Select native / MUI select value
      '& .MuiSelect-select': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
      },
    };

    const buttonSx = {
      height: 34,
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 750,
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
    };

    const bodyCellSx = {
      py: {
        xs: 0.55,
        sm: 0.75,
        md: 0.8,
      },
      px: {
        xs: 0.35,
        sm: 0.65,
        md: 1,
      },
      whiteSpace: {
        xs: 'nowrap',
        md: 'normal',
      },
      color: '#111827',
      fontWeight: 450,
      overflow: {
        xs: 'hidden',
        md: 'visible',
      },
      textOverflow: {
        xs: 'ellipsis',
        md: 'clip',
      },
      wordBreak: {
        xs: 'normal',
        md: 'break-word',
      },
      lineHeight: 1.25,
      fontVariantNumeric: 'tabular-nums',
    };

    const headerCellSx = {
      py: {
        xs: 0.5,
        sm: 0.7,
        md: 0.85,
      },
      px: {
        xs: 0.35,
        sm: 0.65,
        md: 1,
      },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 650,
      lineHeight: 1.15,
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
    };

    if (!catalogsLoaded) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: '#fff',
          }}
        >
          <CircularProgress size={26} />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            px: {
              xs: 0.75,
              sm: 1,
              md: 1.25,
            },
            py: {
              xs: 0.65,
              sm: 0.75,
            },
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1rem',
                    md: '1.05rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Register Return
              </Typography>

              <Typography
                sx={{
                  mt: 0.15,
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.72rem',
                    sm: '0.78rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Find an order, choose return lines and create the return order
              </Typography>
            </Box>

            <AssignmentReturnIcon
              sx={{
                color: '#1a237e',
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: {
              xs: 0.75,
              sm: 1,
              md: 1.25,
            },
            py: {
              xs: 0.75,
              sm: 1,
            },
          }}
        >
          <Stack spacing={1}>
            {returnDlg.err ? (
              <Alert
                severity="error"
                variant="outlined"
                sx={{
                  py: 0.25,
                  fontSize: '0.78rem',
                  borderRadius: '12px',
                }}
              >
                {returnDlg.err}
              </Alert>
            ) : null}

            {Array.isArray(returnDlg.existingReturns) &&
              returnDlg.existingReturns.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: {
                      xs: 1,
                      sm: 1.25,
                    },
                    borderRadius: '14px',
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                    boxShadow: 'none',
                  }}
                >
                  <Stack spacing={1}>
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{
                        py: 0.25,
                        fontSize: '0.78rem',
                        borderRadius: '12px',
                      }}
                    >
                      A return is already registered for this order. Multiple returns are not allowed.
                    </Alert>

                    {returnDlg.existingReturns.map((o) => (
                      <Typography
                        key={o.iref}
                        sx={{
                          color: '#111827',
                          fontWeight: 600,
                          fontSize: {
                            xs: '0.78rem',
                            sm: '0.86rem',
                          },
                        }}
                      >
                        Return #{o.docNumber || o.iref} · {o.createdOn} · Total{' '}
                        {Number(o.totalAmount || 0).toFixed(2)}
                      </Typography>
                    ))}

                    <Stack direction="row" justifyContent="flex-end">
                      <Button variant="contained" onClick={this.resetForm} sx={buttonSx}>
                        Load another order
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}

            {returnDlg.step === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: {
                    xs: 1,
                    sm: 1.25,
                  },
                  borderRadius: '14px',
                  borderColor: 'rgba(15, 23, 42, 0.08)',
                  boxShadow: 'none',
                  overflow: 'hidden',
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SearchIcon fontSize="small" sx={{ color: '#1a237e', opacity: 0.7 }} />
                    <Typography
                      sx={{
                        color: '#1a237e',
                        fontWeight: 800,
                        fontSize: {
                          xs: '0.82rem',
                          sm: '0.9rem',
                        },
                      }}
                    >
                      Find source order
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flexWrap: 'wrap',
                    }}
                  >
                    <TextField
                      label="Order iref"
                      size="small"
                      value={returnDlg.searchIref}
                      onChange={(e) =>
                        this.setState((s) => ({
                          returnDlg: {
                            ...s.returnDlg,
                            searchIref: e.target.value.replace(/\D/g, ''),
                          },
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') this.loadReturnSourceByIref();
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      sx={{
                        width: {
                          xs: 'calc(100% - 92px)',
                          sm: 220,
                        },
                        flexGrow: {
                          xs: 1,
                          sm: 0,
                        },
                        ...fieldSx,
                      }}
                    />

                    <Button
                      variant="contained"
                      onClick={this.loadReturnSourceByIref}
                      disabled={!returnDlg.searchIref || returnDlg.busy}
                      startIcon={<SearchIcon fontSize="small" />}
                      sx={buttonSx}
                    >
                      {returnDlg.busy ? 'Loading...' : 'Load'}
                    </Button>

                    <Button variant="outlined" onClick={this.resetForm} sx={buttonSx}>
                      Clear
                    </Button>
                  </Box>

                  <Typography
                    sx={{
                      color: '#64748b',
                      fontWeight: 600,
                      fontSize: {
                        xs: '0.72rem',
                        sm: '0.78rem',
                      },
                    }}
                  >
                    Enter the original order numeric id.
                  </Typography>
                </Stack>
              </Paper>
            )}

            {returnDlg.step === 1 && (
              <Stack spacing={1}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: {
                      xs: 1,
                      sm: 1.25,
                    },
                    borderRadius: '14px',
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                    boxShadow: 'none',
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <ReceiptLongIcon fontSize="small" sx={{ color: '#1a237e', opacity: 0.7 }} />
                      <Typography
                        sx={{
                          color: '#1a237e',
                          fontWeight: 800,
                          fontSize: {
                            xs: '0.82rem',
                            sm: '0.9rem',
                          },
                        }}
                      >
                        Source order
                      </Typography>
                    </Stack>

                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      <Chip
                        size="small"
                        label={`Order: ${returnDlg.sourceOrder?.code || '-'}${
                          returnDlg.sourceOrder?.iref ? ` (${returnDlg.sourceOrder.iref})` : ''
                        }`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`POS: ${returnDlg.sourceOrder?.pos?.name || '-'}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Date: ${returnDlg.sourceOrder?.date || '-'}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Total: ${Number(returnDlg.sourceOrder?.valueWVat || 0).toFixed(2)}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Discount: ${Number(returnDlg.sourceOrder?.discountValue || 0).toFixed(2)}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: {
                      xs: 1,
                      sm: 1.25,
                    },
                    borderRadius: '14px',
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                    boxShadow: 'none',
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      sx={{
                        color: '#1a237e',
                        fontWeight: 800,
                        fontSize: {
                          xs: '0.82rem',
                          sm: '0.9rem',
                        },
                      }}
                    >
                      Select products to return
                    </Typography>

                    <Stack direction="row" spacing={0.75}>
                      <Button size="small" variant="outlined" onClick={this.fillReturnAll} sx={buttonSx}>
                        Return all
                      </Button>
                      <Button size="small" variant="outlined" onClick={this.clearReturnSelection} sx={buttonSx}>
                        Clear
                      </Button>
                    </Stack>
                  </Stack>

                  <TableContainer
                    sx={{
                      maxHeight: {
                        xs: 300,
                        sm: 320,
                        md: 340,
                      },
                      overflowX: 'auto',
                      overflowY: 'auto',
                      width: '100%',
                      border: '1px solid rgba(15, 23, 42, 0.06)',
                      borderRadius: '12px',
                      '& .MuiTableCell-root': {
                        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                        fontSize: {
                          xs: '0.76rem',
                          sm: '0.86rem',
                          md: '0.92rem',
                        },
                      },
                      '& .MuiTableRow-hover:hover': {
                        backgroundColor: 'rgba(26, 35, 126, 0.035)',
                      },
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        minWidth: {
                          xs: 600,
                          sm: 680,
                          md: '100%',
                        },
                        width: '100%',
                        tableLayout: {
                          xs: 'fixed',
                          md: 'auto',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              ...headerCellSx,
                              position: 'sticky',
                              left: 0,
                              zIndex: 4,
                              width: {
                                xs: 190,
                                sm: 280,
                                md: '44%',
                              },
                            }}
                          >
                            Line
                          </TableCell>
                          {[
                            ['Qty', { xs: 70, sm: 80, md: '11%' }],
                            ['Value', { xs: 82, sm: 95, md: '14%' }],
                            ['Disc.', { xs: 78, sm: 90, md: '13%' }],
                            ['Return', { xs: 120, sm: 135, md: '18%' }],
                          ].map(([label, width]) => (
                            <TableCell
                              key={label}
                              align="right"
                              sx={{
                                ...headerCellSx,
                                width,
                              }}
                            >
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {returnDlg.lines.map((ln) => {
                          const orig = Number(ln.qty || 0);
                          const max = Math.max(0, orig);
                          const cur = Number(returnDlg.select[ln.key] || 0);

                          return (
                            <TableRow key={ln.key} hover>
                              <TableCell
                                sx={{
                                  ...bodyCellSx,
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: '#fff',
                                  fontWeight: 550,
                                  width: {
                                    xs: 190,
                                    sm: 280,
                                    md: '44%',
                                  },
                                }}
                              >
                                {ln.label}
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{
                                  ...bodyCellSx,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {orig}
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{
                                  ...bodyCellSx,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {Number(ln.value || 0).toFixed(2)}
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{
                                  ...bodyCellSx,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {Number(ln.discountValue || 0).toFixed(2)}
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{
                                  ...bodyCellSx,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0, max }}
                                  value={cur}
                                  onChange={(e) => {
                                    const v = Math.max(
                                      0,
                                      Math.min(max, Number(e.target.value || 0))
                                    );
                                    this.setState((s) => ({
                                      returnDlg: {
                                        ...s.returnDlg,
                                        select: { ...s.returnDlg.select, [ln.key]: v },
                                      },
                                    }));
                                  }}
                                  sx={{
                                    width: {
                                      xs: 82,
                                      sm: 92,
                                    },
                                    '& .MuiInputBase-root': {
                                      height: 30,
                                      borderRadius: '10px',
                                      fontSize: {
                                        xs: '16px',
                                        sm: '0.8rem',
                                      },
                                    },
                                    '& input': {
                                      textAlign: 'right',
                                      px: 0.5,
                                    },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: {
                      xs: 1,
                      sm: 1.25,
                    },
                    borderRadius: '14px',
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                    boxShadow: 'none',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography
                      sx={{
                        color: '#1a237e',
                        fontWeight: 800,
                        fontSize: {
                          xs: '0.82rem',
                          sm: '0.9rem',
                        },
                      }}
                    >
                      Return summary
                    </Typography>

                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      <Chip
                        size="small"
                        label={`Selected lines: ${totals.selectedCount}`}
                        color="primary"
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Selected qty: ${totals.totalQty}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Gross: ${totals.gross.toFixed(2)}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`VAT: ${totals.vat.toFixed(2)}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Net: ${totals.net.toFixed(2)}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Discount: ${totals.discount.toFixed(2)}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                    </Stack>

                    {totals.isFullReturnCandidate && (
                      <Alert
                        severity="warning"
                        variant="outlined"
                        sx={{
                          py: 0.25,
                          fontSize: '0.78rem',
                          borderRadius: '12px',
                        }}
                      >
                        No lines selected. Submitting now will create a full return for this order.
                      </Alert>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      <TextField
                        select
                        label="Payment"
                        size="small"
                        value={returnDlg.payType}
                        onChange={(e) =>
                          this.setState((s) => ({
                            returnDlg: {
                              ...s.returnDlg,
                              payType: Number(e.target.value),
                            },
                          }))
                        }
                        SelectProps={{ native: true }}
                        sx={{
                          width: {
                            xs: 160,
                            sm: 180,
                          },
                          ...fieldSx,
                        }}
                      >
                        <option value={1}>Cash</option>
                        <option value={2}>Bank Transfer</option>
                      </TextField>
                    </Box>
                  </Stack>
                </Paper>

                <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      this.setState((s) => ({
                        returnDlg: { ...s.returnDlg, step: 0, err: '', success: null },
                      }))
                    }
                    sx={buttonSx}
                  >
                    Back
                  </Button>

                  <Button
                    variant="contained"
                    color="success"
                    onClick={this.submitReturn}
                    disabled={returnDlg.busy || returnDlg.existingReturns?.length > 0}
                    startIcon={returnDlg.busy ? null : <AssignmentReturnIcon fontSize="small" />}
                    sx={buttonSx}
                  >
                    {returnDlg.busy ? 'Submitting...' : 'Create return'}
                  </Button>
                </Stack>
              </Stack>
            )}

            {returnDlg.step === 2 && success && (
              <Stack spacing={1}>
                <Alert
                  severity="success"
                  icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                  variant="outlined"
                  sx={{
                    py: 0.5,
                    fontSize: '0.82rem',
                    borderRadius: '12px',
                  }}
                >
                  {success?.message || 'Return order created successfully.'}
                </Alert>

                <Paper
                  variant="outlined"
                  sx={{
                    p: {
                      xs: 1,
                      sm: 1.25,
                    },
                    borderRadius: '14px',
                    borderColor: 'success.main',
                    boxShadow: 'none',
                    bgcolor: '#fff',
                  }}
                >
                  <Stack spacing={1.25}>
                    <Typography
                      sx={{
                        color: '#1a237e',
                        fontWeight: 800,
                        fontSize: {
                          xs: '0.82rem',
                          sm: '0.9rem',
                        },
                      }}
                    >
                      Backend response
                    </Typography>

                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      <Chip
                        size="small"
                        color="success"
                        label={`Status: ${success?.status || '-'}`}
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Module: ${success?.module || '-'}`}
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Type: ${success?.type || '-'}`}
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Code: ${success?.code || '-'}`}
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                    </Stack>

                    <Divider />

                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      <Chip
                        size="small"
                        label={`Return order: ${success?.data?.orderIref ?? '-'}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Source order: ${success?.data?.returnedOrderIref ?? '-'}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Return code: ${success?.data?.code ?? '-'}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Payment: ${
                          Number(success?.data?.payType) === 1 ? 'Cash' : 'Bank Transfer'
                        }`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        label={`Total: ${Number(success?.data?.totalAmount || 0).toFixed(2)}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                      <Chip
                        size="small"
                        color={success?.data?.isFullReturn ? 'warning' : 'info'}
                        label={`Full return: ${success?.data?.isFullReturn ? 'Yes' : 'No'}`}
                        variant="outlined"
                        sx={{ borderRadius: '10px', fontWeight: 650 }}
                      />
                    </Stack>

                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{
                        py: 0.5,
                        fontSize: '0.82rem',
                        borderRadius: '12px',
                      }}
                    >
                      <b>{success?.message || 'Completed'}</b>
                      {success?.data?.orderIref
                        ? ` · New return order #${success.data.orderIref}`
                        : ''}
                    </Alert>
                  </Stack>
                </Paper>

                <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<ReplayIcon fontSize="small" />}
                    onClick={() =>
                      this.setState((s) => ({
                        returnDlg: {
                          ...s.returnDlg,
                          step: 1,
                          success: null,
                          err: '',
                        },
                      }))
                    }
                    sx={buttonSx}
                  >
                    Back to lines
                  </Button>

                  <Button
                    variant="contained"
                    onClick={this.resetForm}
                    startIcon={<AssignmentReturnIcon fontSize="small" />}
                    sx={buttonSx}
                  >
                    New return
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    );
  }
}